/**
 * Centralized Entity Types
 * Single source of truth for core entity definitions
 *
 * Usage:
 * import type { User, Contact, Property } from '@/types/entities';
 */

// ===========================================
// User Entity
// ===========================================

export type UserRole =
    | 'admin'
    | 'site_admin'   // top-level operator (legacy `admin` is treated as equivalent)
    | 'devops'       // sees Developer Dock and infra-flavored controls
    | 'user'
    | 'agent';

/** Roles allowed to see the Developer Dock + hit /api/debug/* endpoints.
 *  Keep in sync with `DEV_DOCK_ROLES` in backend/app/api/v1/endpoints/debug.py. */
export const DEV_DOCK_ROLES: readonly UserRole[] = ['admin', 'site_admin', 'devops'] as const;
export type UserStatus = 'active' | 'pending' | 'suspended' | 'inactive';

export interface User {
    id: number;
    email: string;
    name: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    role: UserRole;
    status: UserStatus;
    email_verified: boolean;
    avatar?: string;
    user_type?: string;
    is_active?: boolean;
    created_at: string;
    updated_at?: string;
    // Affordability preferences — populated by /auth/me. Optional so
    // existing call sites that only use id/email/role aren't affected.
    down_payment_mode?: 'percentage' | 'capped' | 'fixed' | null;
    preferred_down_percentage?: number | null;
    max_down_amount?: number | null;
    // Notification preferences — also populated by /auth/me.
    notify_email?: boolean;
    notify_sms?: boolean;
}

export interface UserListParams {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    email_verified?: boolean;
    role?: string;
}

// ===========================================
// Contact Entity
// ===========================================

export type ContactStatus = 'active' | 'banned' | 'deleted';

/** Which record the brokerage block was read from. */
export type ContactBrokerageSource = 'position' | 'agent_config' | 'realtor_company';

/**
 * The brokerage a person hangs their licence with: the company row of
 * the position held now, else (the owner's own contact) Settings →
 * Agent Profile's broker fields, else the free-text company. Every key
 * is present; unknown ones are null.
 */
export interface ContactBrokerage {
    company_id: number | null;
    company_name: string | null;
    title: string | null;
    license_number: string | null;
    license_state: string | null;
    phone: string | null;
    source: ContactBrokerageSource;
    /** The brokerage's logo for the agent banner (our own falls back to the site brand's). */
    logo_url?: string | null;
    /** Its main colour, for the banner strip. */
    color?: string | null;
    /** The site's own brokerage: its agents aren't leads by default. */
    same_brokerage?: boolean;
}

/** `lead_state.contact_kind`: the one kind the lead rules go by. */
export type ContactKind = 'client' | 'agent' | 'broker' | 'service_pro' | 'service' | 'company' | 'self';

/** Who decided the lead state: the operator, detection, or the default for the kind. */
export type LeadStateSource = 'you' | 'automatic' | 'default';

export type LeadOverride = 'lead' | 'not_lead';

/** Why detection thinks a contact is (or isn't) discussing a property. */
export interface LeadEvidence {
    kind: 'mls' | 'property' | 'zillow' | 'ai' | 'ai_device';
    address?: string | null;
    mls_number?: string | null;
    url?: string | null;
    status?: string | null;
    reason?: string | null;
    verdict?: LeadOverride;
    model?: string | null;
    at?: string;
}

/** An agent's or broker's licence (number null when it's missing). */
export interface ContactLicense {
    number: string | null;
    state: string | null;
    kind: 'agent' | 'broker';
}

export interface Contact {
    id: number;
    // Name fields - support both formats
    name?: string;
    first_name?: string;
    last_name?: string;
    // Contact info
    email: string;
    phone?: string;
    phone_number?: string; // Legacy field name
    date_of_birth?: string;
    first_contacted?: string;
    // Home / mailing address (any contact); REWS for Android maps by it.
    street_address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    // Classification
    contact_type?: string;
    relationship_type?: string;
    is_buyer?: boolean;
    is_seller?: boolean;
    is_renter?: boolean;
    is_landlord?: boolean;
    is_agent?: boolean;
    is_realtor?: boolean;
    is_broker?: boolean;
    is_service_professional?: boolean;
    /** A lead-gen service, automated sender or other business that texts us. Never auto-replied to. */
    is_service?: boolean;
    is_voucher_participant?: boolean;
    is_self?: boolean;
    is_company?: boolean;
    company_name?: string;
    display_name?: string;
    is_banned?: boolean;
    // Sponsored = paid (or comped) for slot-1 placement in the
    // AI-chat lender-referral cards. Surfaces a yellow badge on
    // the contact card so the operator can see it at a glance.
    is_sponsored?: boolean;
    realtor_license_number?: string;
    realtor_company?: string;
    broker_license_number?: string;
    broker_company?: string;
    realtor_verified?: boolean;
    /** Only on GET /contacts/{id}; null for a company or when nothing is known. */
    brokerage?: ContactBrokerage | null;
    service_profession?: string;
    service_license_number?: string;
    service_company?: string;
    office_address?: string;
    office_phone?: string;
    household_size?: number;
    eligible_counties?: Record<string, unknown> | string[] | string;
    income_limit?: number;
    source?: string;
    contact_where?: string;
    development_use?: boolean;
    access_state?: number;
    access_note?: string;
    state_updated_at?: string;
    related_contact_ids?: string;
    // Metadata
    notes?: string;
    status: ContactStatus;
    created_at: string;
    updated_at?: string;
    last_contacted?: string;
    deleted?: boolean;
    // Computed by the backend (see /api/contacts/_compute_lead_priority).
    // Score is 0–60, tier maps it to a four-band color. Read-only on
    // the frontend; recomputed on every list/detail fetch.
    lead_priority?: {
        score: number;
        tier: 'hot' | 'warm' | 'cool' | 'cold';
        signals: string[];
    };
    // Set by an open house sign-in for unrepresented visitors; feeds the
    // Potential Leads "Open house visitors" section. Cleared only by the
    // operator ("Not a lead"), never by the sign-in flow.
    is_potential_lead?: boolean;
    potential_lead_at?: string | null;
    // Lead state (backend services/lead_state.py): agents at our
    // brokerage and service pros aren't leads unless they're discussing
    // a property or the operator says so.
    contact_kind?: ContactKind;
    is_lead?: boolean;
    lead_state_source?: LeadStateSource;
    lead_override?: LeadOverride | null;
    lead_signal?: LeadOverride | null;
    lead_reason?: string | null;
    lead_evidence?: LeadEvidence[];
    lead_assessed_at?: string | null;
    same_brokerage?: boolean;
    /** Agents and brokers only; every one needs a number. */
    license?: ContactLicense | null;
    license_missing?: boolean;
    // Company rows: the brokerage's site and logo (for its agents' banner).
    website_url?: string | null;
    logo_url?: string | null;
    logo_source?: 'upload' | 'fetched' | null;
    brand_color?: string | null;
}

/** One `{ button, label }` row of a company's phone-menu cheat sheet. */
export interface PhoneMenuCheatsheetEntry {
    button: string;
    label: string;
}

/** Where a service company operates; see `contacts.service_areas` on the backend. */
export interface ContactServiceAreas {
    all_us: boolean;
    states: string[];
    counties: { state: string; name: string }[];
    zip_codes: string[];
}

/**
 * Body of `POST /contacts`. Mirrors the backend `ContactCreate` schema
 * (`backend/app/api/v1/endpoints/contacts.py`): every field optional
 * except `source`. A person needs a first name, last name or email; a
 * company (`is_company`) needs a `company_name` and no person's name.
 */
export interface CreateContactDTO {
    first_name?: string | null;
    last_name?: string | null;
    email?: string;
    phone_number?: string;
    date_of_birth?: string;
    first_contacted?: string;
    last_contacted?: string;
    source: string;
    contact_where?: string;

    // Client types
    is_buyer?: boolean;
    is_renter?: boolean;
    is_seller?: boolean;
    is_landlord?: boolean;
    is_potential_lead?: boolean;
    /** The operator's own call; null = decide automatically. */
    lead_override?: LeadOverride | null;
    /** A company's website (its logo is found there). */
    website_url?: string | null;
    potential_lead_at?: string | null;

    // Companies: `company_name` is the display label when `is_company`.
    is_company?: boolean;
    company_name?: string | null;
    sales_email?: string;
    support_email?: string;
    phone_menu_cheatsheet?: PhoneMenuCheatsheetEntry[];
    business_owner_contact_id?: number;
    business_category?: string;
    has_license?: boolean;
    license_number?: string;
    license_type?: string;
    license_state?: string;
    license_expires_at?: string;
    has_insurance?: boolean;
    insurance_carrier?: string;
    insurance_policy_number?: string;
    insurance_type?: string;
    insurance_coverage_amount?: number;
    insurance_expires_at?: string;
    service_areas?: ContactServiceAreas;

    // Real estate professionals
    is_realtor?: boolean;
    is_broker?: boolean;
    realtor_license_number?: string;
    realtor_company?: string;
    broker_license_number?: string;
    broker_company?: string;
    realtor_verified?: boolean;

    // Service professionals
    is_service_professional?: boolean;
    service_profession?: string;
    service_license_number?: string;
    service_company?: string;
    office_address?: string;
    office_phone?: string;
    /** Home / mailing address (any contact); REWS for Android maps by it. */
    street_address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;

    // "Service" senders: lead-gen services, automated senders, businesses.
    is_service?: boolean;

    // Housing-voucher participants
    is_voucher_participant?: boolean;
    household_size?: number;
    eligible_counties?: Record<string, unknown>;
    income_limit?: number;

    development_use?: boolean;
    access_state?: number;
    access_note?: string;
}

export interface UpdateContactDTO extends Partial<CreateContactDTO> {
    status?: ContactStatus;
}

/**
 * Query of `GET /contacts`. The backend pages with `skip` / `limit`
 * (max 1000); `page` / `per_page` are translated by `contactsApi`.
 */
export interface ContactListParams {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    relationship_type?: string;
    include_deleted?: boolean;
    limit?: number;
    skip?: number;
    /** Only contacts flagged by an open house sign-in (`is_potential_lead`). */
    potential_lead?: boolean;
    /** Only "Service" senders (lead-gen services, automated senders). */
    is_service?: boolean;
    /** Only company rows (`is_company`). */
    is_company?: boolean;
    /** The effective lead state (`is_lead`). */
    is_lead?: boolean;
    /** Agents and brokers saved without their licence number. */
    license_missing?: boolean;
}

// ===========================================
// Property Entity
// ===========================================

export type PropertyStatus = 'FOR_SALE' | 'Active' | 'Pending' | 'Sold' | 'Off Market';

export interface PropertyLocation {
    address: string;
    city: string;
    state: string;
    zip: string;
    county?: string;
    latitude?: string;
    longitude?: string;
}

export interface PropertyAgent {
    name: string;
    email?: string;
    phone?: string;
    office_phone?: string;
    title?: string;
    mls_agentid?: string;
}

export interface PropertyOffice {
    name?: string;
    listing_office?: string;
    company?: string;
}

export interface PropertyImageUrls {
    full_url?: string;
    small_url?: string;
    gallery_url?: string;
    thumb_url?: string;
}

export interface PropertyImages {
    main_image?: PropertyImageUrls;
}

export interface PropertyBathroomDetails {
    full_baths?: number;
    half_baths?: number;
    total_bathrooms?: number;
    bathrooms_display?: number;
}

export interface Property {
    id: number;
    listing_id: string;
    title?: string;
    // Pricing
    display_price?: string;
    list_price?: number;
    price?: number;
    // Basic info
    bedrooms?: number;
    bathrooms?: number;
    square_feet?: string;
    actual_sqft?: number;
    // Status and metadata
    status?: PropertyStatus | string;
    days_on_market?: number;
    year_built?: number;
    property_type?: string;
    mls_number?: string;
    mls_name?: string;
    // Features
    garage_spaces?: number;
    lot_sqr_footage?: number;
    acreage?: number;
    virtual_tour_url?: string;
    url_slug?: string;
    comments?: string;
    description?: string;
    // Flags
    new_construction?: boolean;
    pool?: boolean;
    senior_community?: boolean;
    // Dates
    created_date?: string;
    listed_date?: string;
    created_at?: string;
    updated_at?: string;
    // Related
    subdivision?: string;
    location?: PropertyLocation;
    full_address?: string;
    agent?: PropertyAgent;
    office?: PropertyOffice;
    images?: PropertyImages;
    bathroom_details?: PropertyBathroomDetails;
    features?: string[];
}

export interface PropertyListParams {
    page?: number;
    per_page?: number;
    /**
     * Only listings we may advertise: our brokerage's, or another broker's
     * with written permission on file. The featured list and the social
     * editor's listing picker pass it; the public search grid does not.
     */
    advertisable?: boolean;
    min_price?: number;
    max_price?: number;
    bedrooms?: number;
    bathrooms?: number;
    property_type?: string;
    city?: string;
    state?: string;
    status?: string;
    new_construction?: boolean;
    pool?: boolean;
    senior_community?: boolean;
    min_sqft?: number;
    max_sqft?: number;
    min_acreage?: number;
    max_acreage?: number;
    garage_spaces?: number;
    search?: string;
    // Public filter-bar shorthand — these mirror the backend query params
    // the new /properties endpoint accepts and are used by the public
    // properties-for-sale page filter UI.
    min_beds?: number;
    min_baths?: number;
    min_year_built?: number;
    min_lot_sqft?: number;
    sort?: PropertySortKey;
    // Browse-page category tab — backend maps these to property_type
    // ILIKE patterns: 'for_sale' (default), 'for_rent', 'land',
    // 'commercial'. Missing/unknown values fall through to 'for_sale'.
    category?: 'for_sale' | 'for_rent' | 'land' | 'commercial';
    // Location-aware filters. When `near_lat`/`near_lon` are supplied
    // the backend ranks results by proximity; `within_miles` adds a
    // hard radius filter on top. Coming from the location-consent flow.
    near_lat?: number;
    near_lon?: number;
    within_miles?: number;
    // Public surfaces default-hide SOLD / RECENTLY_SOLD / OFF_MARKET.
    // Set true to opt back in (admin grids always do; the public
    // filter bar exposes a checkbox).
    include_sold?: boolean;
}

export type PropertySortKey =
    | 'newest'
    | 'oldest'
    | 'price_asc'
    | 'price_desc'
    | 'beds_desc'
    | 'sqft_desc'
    | 'days_asc';

// ===========================================
// Contact Exchange Entity
// ===========================================

export type ExchangeStatus = 'pending' | 'approved' | 'rejected' | 'contacted' | 'completed';

export interface ContactExchange {
    id: number;
    requester_id?: number;
    requester_name?: string;
    requester_email?: string;
    target_id?: number;
    target_name?: string;
    target_email?: string;
    // Contact info (from admin view)
    name?: string;
    email?: string;
    phone?: string;
    // Details
    status: ExchangeStatus | string;
    message?: string;
    note?: string;
    // Property preferences
    transaction_type?: string;
    property_types?: string[];
    locations?: string[];
    budget_min?: number;
    budget_max?: number;
    timeline?: string;
    email_verified?: boolean;
    // Dates
    created_at: string;
    updated_at?: string;
}

// ===========================================
// Agreement Entity
// ===========================================

export type AgreementStatus = 'draft' | 'sent' | 'viewed' | 'signed' | 'cancelled' | 'pending_approval' | 'approved' | 'sent_to_dotloop' | 'dotloop_send_failed';

export interface Agreement {
    id: number;
    template_name: string;
    customer_name: string;
    customer_email: string;
    customer_phone?: string;
    status: AgreementStatus;
    pdf_path?: string;
    property_id?: number;
    property_address?: string;
    created_at: string;
    updated_at: string;
    approval_email_sent_at?: string;
    approval_viewed_at?: string;
    approved_at?: string;
    dotloop_loop_id?: number;
    dotloop_profile_id?: number;
    dotloop_document_id?: number;
    dotloop_loop_url?: string;
    dotloop_sent_at?: string;
}

// ===========================================
// Affiliate Entity
// ===========================================

export type AffiliateStatus = 'active' | 'pending' | 'inactive';

export interface Affiliate {
    id: number;
    name: string;
    email: string;
    referral_code: string;
    total_referrals: number;
    successful_referrals: number;
    commission_earned: number;
    status: AffiliateStatus;
    created_at: string;
}

// ===========================================
// Relationship Type Entity
// ===========================================

export interface RelationshipType {
    id: number;
    name: string;
    relationship_name?: string;
    description?: string;
}

// ===========================================
// Open House Entity
// ===========================================
//
// Mirrors backend/app/models/open_house.py. `OpenHouse` and
// `OpenHouseSignIn` list every key of the model's `to_dict()`; the phase
// is derived server-side on every read, never stored.

export type OpenHousePhase = 'upcoming' | 'live' | 'sign_in_open' | 'closed' | 'cancelled';
export type OpenHouseStatus = 'scheduled' | 'cancelled';
export type OpenHouseListingType = 'sale' | 'rent';
export type OpenHouseSource = 'zillow_url' | 'address' | 'manual_address' | 'copy_open_house' | 'copy_property';
export type OpenHouseEntryMode = 'kiosk' | 'phone' | 'admin_manual';
/** Entry modes the public sign-in endpoint accepts. */
export type OpenHousePublicEntryMode = Exclude<OpenHouseEntryMode, 'admin_manual'>;
export type AgentVerificationStatus =
    | 'not_applicable'
    | 'verified'
    | 'not_found'
    | 'override'
    | 'non_nj'
    | 'lookup_error';
/** Packet email + stats email outcome. */
export type OpenHouseDeliveryStatus = 'pending' | 'sent' | 'skipped' | 'failed';
export type OpenHouseFollowupStatus = 'pending' | 'sending' | 'sent' | 'skipped' | 'failed' | 'cancelled';
export type OpenHouseFollowupMode = 'vero' | 'static';
/**
 * Derived at sign-in: who the visitor is to the listing team.
 * `agent_not_nj_licensed` = they named an agent licensed outside NJ, so
 * they are treated as unrepresented (potential client); `visiting_agent`
 * = the visitor is a licensee previewing the home.
 */
export type OpenHouseRepresentation = 'unrepresented' | 'represented' | 'agent_not_nj_licensed' | 'visiting_agent';
/**
 * Whether a self-declared visiting-agent claim is backed by evidence: a
 * business-card photo, a licence confirmed on the NJ search, or the
 * operator's word. `pending` = held open until `agent_proof_due_at`;
 * `expired` = it lapsed and the row was downgraded to a visitor. Null on
 * every row that never claimed to be an agent.
 */
export type OpenHouseAgentProofStatus = 'proven' | 'pending' | 'expired';
/** How the server chases an unproven claim for the business card. */
export type OpenHouseAgentReminderChannel = 'sms' | 'email';

export interface OpenHouseOption {
    value: string;
    label: string;
}

export interface OpenHouseDocument {
    index: number;
    label: string | null;
    filename: string | null;
    content_type: string | null;
    size_bytes: number | null;
    sha256: string | null;
    uploaded_at: string | null;
}

export interface OpenHouse {
    id: number;
    public_code: string;
    /** Soft link to properties.listing_id (zpid or 'oh-<code>'). */
    property_listing_id: string;
    title: string | null;
    full_address: string | null;
    /** Street line only, e.g. "12 Oak St". */
    property_short: string;
    listing_type: OpenHouseListingType;
    // Naive-UTC ISO strings — parse with utils/dateTime.parseApiDate.
    starts_at: string;
    ends_at: string;
    timezone: string;
    sign_in_closes_at: string;
    closed_reason: string | null;
    /** Server-rendered "Sat, Sep 12 · 1:00–4:00 PM ET". */
    schedule_label: string;
    status: OpenHouseStatus;
    phase: OpenHousePhase;
    sign_in_path: string;
    sign_in_url: string;
    source: OpenHouseSource;
    source_url: string | null;
    copied_from_open_house_id: number | null;
    listing_agent_name: string | null;
    listing_agent_email: string | null;
    listing_agent_phone: string | null;
    listing_brokerage: string | null;
    notes: string | null;
    documents: OpenHouseDocument[];
    /** Operator-written lines for the kiosk idle carousel ("New roof 2023"). */
    kiosk_highlights: string[];
    stats_email_status: OpenHouseDeliveryStatus;
    stats_email_to: string | null;
    stats_email_sent_at: string | null;
    stats_email_attempts: number;
    stats_email_error: string | null;
    stats_snapshot: Record<string, unknown> | null;
    created_by: number | null;
    created_at: string;
    updated_at: string | null;
}

export interface OpenHouseSignIn {
    id: number;
    open_house_id: number;
    client_submission_id: string | null;
    contact_id: number | null;
    agent_contact_id: number | null;
    entry_mode: OpenHouseEntryMode;
    duplicate_of_id: number | null;
    first_name: string;
    last_name: string;
    display_name: string;
    email: string | null;
    phone: string | null;
    how_did_you_hear: string | null;
    timeframe: string | null;
    interested_in_property: boolean;
    comments: string | null;
    is_working_with_agent: boolean;
    agent_first_name: string | null;
    agent_last_name: string | null;
    agent_display_name: string;
    agent_brokerage: string | null;
    agent_refnum: string | null;
    agent_license_type: string | null;
    agent_office_license_refnum: string | null;
    agent_verification_status: AgentVerificationStatus;
    sms_consent: boolean;
    sms_consent_version: string | null;
    cis_template: string | null;
    cis_sha256: string | null;
    cis_acknowledged_at: string | null;
    has_signature_image: boolean;
    typed_signature: string | null;
    signed_at: string;
    client_signed_at: string | null;
    late_replay: boolean;
    has_signin_pdf: boolean;
    has_cis_pdf: boolean;
    packet_email_status: OpenHouseDeliveryStatus;
    packet_email_attempts: number;
    packet_email_sent_at: string | null;
    packet_email_error: string | null;
    packet_email_skip_reason: string | null;
    is_potential_client: boolean;
    followup_scheduled_for: string | null;
    followup_status: OpenHouseFollowupStatus;
    followup_attempts: number;
    followup_sent_at: string | null;
    followup_sid: string | null;
    followup_mode: OpenHouseFollowupMode | null;
    followup_error: string | null;
    followup_skip_reason: string | null;
    /** Set once the visitor's personal data was erased (tombstone). */
    pii_erased_at: string | null;
    pii_erased_reason: string | null;
    /**
     * Set once the contact details (phone, email, comments, business card)
     * were removed while the signed evidence (name, CIS acknowledgment,
     * signature, PDFs) was kept for the retention period.
     */
    contact_minimized_at: string | null;
    /** The visitor typed their name instead of drawing a signature. */
    has_signature_fallback: boolean;
    /** The visitor is a licensee previewing the home (no CIS, never a lead). */
    visitor_is_agent: boolean;
    representation: OpenHouseRepresentation;
    has_business_card: boolean;
    business_card_uploaded_at: string | null;
    /** image/jpeg | image/png | image/webp | image/heic | image/heif (HEIC cannot render in <img>). */
    business_card_content_type: string | null;
    agent_proof_status: OpenHouseAgentProofStatus | null;
    /** When an unproven claim lapses to an ordinary visitor. */
    agent_proof_due_at: string | null;
    agent_proof_reminder_at: string | null;
    agent_proof_reminder_channel: OpenHouseAgentReminderChannel | null;
    /** Set when the claim lapsed, or the operator corrected the tick. */
    agent_downgraded_at: string | null;
    created_at: string;
}

export interface OpenHouseHalfHourBucket {
    label: string;
    count: number;
}

export interface OpenHouseBrokerageCount {
    name: string;
    count: number;
}

/** Aggregates only — exactly what the listing agent's stats email shows. */
export interface OpenHouseStats {
    total: number;
    unrepresented: number;
    represented: number;
    verified_agents: number;
    /** Visitors who are themselves licensees (excluded from the lead counts). */
    agent_visitors: number;
    /** Visiting agents whose own licence was found on the NJ search. */
    verified_visiting_agents: number;
    /** Visitors whose agent is licensed outside NJ (treated as unrepresented). */
    agent_not_nj: number;
    by_representation: Record<OpenHouseRepresentation, number> | Record<string, number>;
    interested: number;
    sms_consent: number;
    potential_clients: number;
    by_source: Record<string, number>;
    by_timeframe: Record<string, number>;
    by_entry_mode: Record<string, number>;
    by_half_hour: OpenHouseHalfHourBucket[];
    brokerages: OpenHouseBrokerageCount[];
}

/**
 * GET /open-houses/upcoming row — the PUBLIC projection of an open house
 * (no listing-agent contact details, notes, documents or email state).
 * Exactly the keys the backend's public list serialiser emits.
 */
export interface UpcomingOpenHouse {
    id: number;
    public_code: string;
    title: string | null;
    full_address: string | null;
    property_short: string;
    listing_type: OpenHouseListingType;
    starts_at: string;
    ends_at: string;
    timezone: string;
    schedule_label: string;
    phase: OpenHousePhase;
    sign_in_path: string;
    sign_in_url: string;
    status: OpenHouseStatus;
    property: Property | null;
}

/** GET /open-houses (admin) row. */
export type OpenHouseListRow = OpenHouse & {
    property: Property | null;
    sign_ins_count: number;
    unrepresented_count: number;
    verified_agent_count: number;
    followups_pending: number;
    followups_failed: number;
};

/** GET /open-houses/{id}. */
export interface OpenHouseDetail {
    open_house: OpenHouse;
    property: Property | null;
    sign_ins: OpenHouseSignIn[];
    stats: OpenHouseStats;
}

/** Fields the create form reveals when the address has no Zillow match. */
export interface OpenHouseManualProperty {
    address: string;
    city: string;
    state: string;
    zip_code: string;
    list_price?: number;
    bedrooms?: number;
    bathrooms?: number;
    square_feet?: number;
    property_type?: string;
    listing_type?: OpenHouseListingType;
    image_url?: string;
    agent_name?: string;
    agent_email?: string;
    agent_phone?: string;
    brokerage_name?: string;
}

/** POST /open-houses. Local times are 'YYYY-MM-DDTHH:MM' in `timezone`. */
export interface OpenHouseCreateRequest {
    source: OpenHouseSource;
    url?: string;
    address?: string;
    manual_property?: OpenHouseManualProperty;
    copied_from_open_house_id?: number;
    property_listing_id?: string;
    starts_at_local: string;
    ends_at_local: string;
    timezone?: string;
    listing_type?: OpenHouseListingType;
    listing_agent_name?: string;
    listing_agent_email?: string;
    listing_agent_phone?: string;
    listing_brokerage?: string;
    notes?: string;
    kiosk_highlights?: string[];
}

export interface OpenHouseCreateResponse {
    open_house: OpenHouse;
    property: Property | null;
    sign_in_url: string;
    qr_svg: string;
}

/** PATCH /open-houses/{id} — every field optional; `status` only cancels. */
export interface OpenHouseUpdateRequest {
    starts_at_local?: string;
    ends_at_local?: string;
    timezone?: string;
    listing_agent_name?: string | null;
    listing_agent_email?: string | null;
    listing_agent_phone?: string | null;
    listing_brokerage?: string | null;
    notes?: string | null;
    status?: 'cancelled';
    kiosk_highlights?: string[];
}

/** Shape of PATCH / close / send-stats / documents responses. */
export interface OpenHouseMutationResponse {
    open_house: OpenHouse;
}

/** GET /open-houses/{id}/qr. */
export interface OpenHouseQrResponse {
    svg: string;
    url: string;
    code: string;
}

/** Visitor-facing document entry — labels only, never object keys. */
export interface PublicOpenHouseDocument {
    index: number;
    label: string;
    content_type: string | null;
    size_bytes: number | null;
}

/** The open house as the kiosk sees it (no agent contact details). */
export interface PublicOpenHouseSummary {
    public_code: string;
    title: string | null;
    full_address: string | null;
    property_short: string;
    listing_type: OpenHouseListingType;
    starts_at: string;
    ends_at: string;
    timezone: string;
    sign_in_closes_at: string;
    schedule_label: string;
    phase: OpenHousePhase;
    is_sign_in_open: boolean;
    closed_reason: string | null;
    documents: PublicOpenHouseDocument[];
}

/** GET /open-houses/public/{code} — polled by the kiosk every 60 s. */
export interface PublicOpenHouseBootstrap {
    open_house: PublicOpenHouseSummary;
    property: Property | null;
    /** Server clock (ISO) so the countdown ignores tablet clock drift. */
    server_time: string;
    cis: {
        filename: string;
        url: string;
        sha256: string;
    };
    consent: {
        version: string;
        text: string;
    };
    options: {
        how_did_you_hear: OpenHouseOption[];
        timeframes: OpenHouseOption[];
        brokerages: OpenHouseOption[];
    };
    branding: {
        agent_name: string;
        brokerage: string;
        office_phone: string;
        agent_phone: string;
    };
    cis_ack_text: string;
    /** Idle-carousel content: label/value facts about the home (no PII). */
    facts: PublicOpenHouseFact[];
    photos: string[];
    description: string | null;
    highlights: string[];
    kiosk: {
        /** Seconds without a touch before the idle carousel starts. */
        idle_seconds: number;
        /** Seconds per carousel slide. */
        slide_seconds: number;
    };
}

export interface PublicOpenHouseFact {
    label: string;
    value: string;
}

export interface OpenHouseSignInAgentPayload {
    first_name: string;
    last_name: string;
    brokerage?: string;
    refnum?: string;
    license_type?: string;
    office_license_refnum?: string;
    verification_status: AgentVerificationStatus;
    lookup_json?: Record<string, unknown>;
}

/** POST /open-houses/public/{code}/sign-in body. Phone OR email required. */
export interface OpenHouseSignInPayload {
    /** crypto.randomUUID() minted per submission; makes PWA replays idempotent. */
    client_submission_id: string;
    client_signed_at?: string;
    entry_mode: OpenHousePublicEntryMode;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    is_working_with_agent: boolean;
    agent?: OpenHouseSignInAgentPayload;
    how_did_you_hear?: string;
    timeframe?: string;
    interested_in_property: boolean;
    comments?: string;
    sms_consent: boolean;
    sms_consent_version?: string;
    /** Always true for consumers; may be false only when `visitor_is_agent`. */
    cis_acknowledged: boolean;
    /** PNG data URL from SignaturePad (<= 300 KB). */
    signature_png?: string;
    typed_signature?: string;
    /**
     * The visitor is a real estate agent previewing the home: the agent
     * block then describes the visitor themselves, and the CIS
     * acknowledgment + signature are not required.
     */
    visitor_is_agent?: boolean;
    /** Social short-link code the visitor arrived with (attribution only). */
    ref?: string;
}

export interface OpenHouseSignInResponse {
    /**
     * Null when the submission collapsed onto another visitor's existing
     * row (`duplicate` is then true and no token is issued) — the kiosk
     * shows its "You already signed in today" confirmation.
     */
    sign_in_id: number | null;
    duplicate: boolean;
    /** The submitted first name (also on a duplicate). */
    first_name: string;
    followup_scheduled_for: string | null;
    packet_email_queued: boolean;
    /**
     * Opaque per-visitor token (business-card upload). Present when this
     * submission created the row or replayed its own submission id;
     * absent when the sign-in collapsed onto an earlier visitor's row.
     */
    visitor_token?: string;
    visitor_is_agent: boolean;
    representation: OpenHouseRepresentation;
    /**
     * The visiting-agent claim's state. `pending` means the kiosk should
     * still ask for the business card; `proven` means it is settled.
     */
    agent_proof_status: OpenHouseAgentProofStatus | null;
    agent_proof_due_at: string | null;
    /** How the server will chase the card when it is still owed. */
    agent_proof_reminder_channel: OpenHouseAgentReminderChannel | null;
    /** Only for visiting agents (and only with a visitor_token): the phone-upload page path / URL and its QR. */
    business_card_upload_path?: string;
    business_card_upload_url?: string;
    business_card_qr_svg?: string;
}

export interface OpenHouseBusinessCardUpload {
    uploaded: boolean;
    content_type: string;
    size_bytes: number;
    uploaded_at: string;
}

export interface OpenHouseBusinessCardStatus {
    uploaded: boolean;
    uploaded_at: string | null;
}

// ── Phase 2: operator settings, follow-up queue, admin walk-ins, events ──

export type OpenHouseFollowupModeSetting = 'vero' | 'static' | 'off';

export interface OpenHousePlaceholder {
    token: string;
    label: string;
}

/** GET /open-houses/settings (open_house_settings singleton + context). */
export interface OpenHouseSettings {
    sign_in_close_minutes: number;
    followup_mode: OpenHouseFollowupModeSetting;
    followup_delay_minutes: number;
    quiet_hours_start_hour: number;
    quiet_hours_end_hour: number;
    quiet_hours_resume_hour: number;
    followup_repeat_cooldown_days: number;
    opener_template: string;
    packet_email_subject: string;
    packet_email_body: string;
    /** Documents-only wording used for visiting agents (no CIS / record attached). */
    agent_packet_email_body: string;
    stats_email_subject: string;
    stats_email_body: string;
    stats_email_enabled: boolean;
    stats_email_cc_operator: boolean;
    stats_include_brokerages: boolean;
    attach_max_bytes: number;
    public_list_days_ahead: number;
    /** How long an unproven visiting-agent claim is held open. */
    agent_proof_grace_hours: number;
    agent_proof_reminder_enabled: boolean;
    agent_proof_reminder_subject: string;
    agent_proof_reminder_body: string;
    /** Must contain "STOP" — it is an outbound text. */
    agent_proof_reminder_sms: string;
    kiosk_idle_seconds: number;
    kiosk_slide_seconds: number;
    updated_at: string | null;
    /** Tokens the templates understand (server is the source of truth). */
    placeholders: OpenHousePlaceholder[];
    /** Vero (AI SMS auto-reply) master state, so the tab can warn when
     *  `followup_mode` is 'vero' but Vero itself is switched off. */
    vero_enabled: boolean;
}

export type OpenHouseSettingsUpdate = Partial<Omit<OpenHouseSettings, 'updated_at' | 'placeholders' | 'vero_enabled'>>;

export interface OpenHouseSettingsPreviewRequest {
    opener_template: string;
    packet_email_subject: string;
    packet_email_body: string;
    agent_packet_email_body?: string;
    agent_proof_reminder_subject?: string;
    agent_proof_reminder_body?: string;
    agent_proof_reminder_sms?: string;
    stats_email_subject: string;
    stats_email_body: string;
}

/** POST /open-houses/settings/preview — rendered against a sample visitor. */
export interface OpenHouseSettingsPreview {
    opener: string;
    packet_email_subject: string;
    packet_email_body: string;
    packet_email_html: string;
    agent_packet_email_body: string;
    agent_packet_email_html: string;
    agent_proof_reminder_subject: string;
    agent_proof_reminder_body: string;
    agent_proof_reminder_html: string;
    agent_proof_reminder_sms: string;
    stats_email_subject: string;
    stats_email_body: string;
    stats_email_html: string;
}

/** GET /open-houses/followups row — a sign-in plus the event it belongs to. */
export type OpenHouseFollowupRow = OpenHouseSignIn & {
    open_house: Pick<OpenHouse, 'id' | 'public_code' | 'title' | 'full_address' | 'property_short' | 'starts_at' | 'ends_at' | 'schedule_label' | 'timezone'>;
};

/** POST /open-houses/{id}/sign-ins (walk-in typed by the host). Consent
 *  and CIS acknowledgment come from the paper sheet the visitor signed. */
export interface OpenHouseAdminSignInPayload {
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    is_working_with_agent: boolean;
    agent?: OpenHouseSignInAgentPayload;
    how_did_you_hear?: string;
    timeframe?: string;
    interested_in_property: boolean;
    comments?: string;
    sms_consent: boolean;
    cis_acknowledged: boolean;
    typed_signature?: string;
    signed_at?: string;
    visitor_is_agent?: boolean;
}

export interface OpenHouseAdminSignInResponse extends OpenHouseSignInResponse {
    sign_in: OpenHouseSignIn;
}

export interface OpenHouseSignInMutationResponse {
    sign_in: OpenHouseSignIn;
}

/**
 * POST /open-houses/sign-ins/{id}/erase body. The server answers 409
 * inside the six-year record retention window unless `force` is true.
 */
export interface OpenHouseEraseRequest {
    reason?: string;
    force?: boolean;
}

export type OpenHouseSignInPdfKind = 'signin' | 'cis';

/** GET /open-houses/retention — sign-ins past the 6-year record retention period. */
export interface OpenHouseRetentionResponse {
    retention_years: number;
    sign_ins: OpenHouseFollowupRow[];
}

/** WebSocket `openhouse:signin` — ids and flags only, never visitor details. */
export interface OpenHouseSignInEvent {
    open_house_id: number;
    public_code: string;
    sign_in_id: number;
    entry_mode: OpenHouseEntryMode;
    is_working_with_agent: boolean;
    is_potential_client: boolean;
    agent_verification_status: AgentVerificationStatus;
    late_replay: boolean;
    followup_status: OpenHouseFollowupStatus;
    signed_at: string | null;
}

/** WebSocket `openhouse:job` — the worker finished a packet / stats / follow-up. */
export interface OpenHouseJobEvent {
    job: 'packet' | 'stats' | 'followup';
    open_house_id: number;
    sign_in_id: number | null;
    status: string | null;
    ok: boolean;
    at: string;
}

/** WebSocket `openhouse:card` — a visiting agent's business card arrived. */
export interface OpenHouseCardEvent {
    open_house_id: number;
    sign_in_id: number;
    uploaded_at: string;
}
